/**
 * Phase 8 · Cluster C-43 — Naukri sourcing connector.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md Addendum F §33.7
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §8.7
 *
 * Read-only candidate discovery against Naukri's REST search endpoint.
 * auth_kind='api_key' is the only supported path today; the UI doesn't
 * offer anything else. As with LinkedIn, the real network call is routed
 * through the Supabase Edge Function proxy (`ggj_b2b_sourcing_proxy`) so
 * the api_key stays out of the browser; if that function is not
 * deployed yet we raise a ConnectorTransportError that the runner turns
 * into a clean chip + tool_result.ok=false.
 *
 * Rate limiting: in-memory daily bucket at 500 searches / day / org.
 * Daily window vs LinkedIn's hourly because Naukri's published limits
 * are much looser — we still keep a ceiling so a runaway agent loop
 * can't rack up provider cost in one tab session.
 */
import { supabase } from '@core/supabase/client';
import { sourceConnectorsService } from '@core/supabase/services/sourceConnectorsService';
import {
  clampLimit,
  ConnectorNotConfiguredError,
  ConnectorTransportError,
  type ConnectorHealth,
  type SourcingConnector,
  type SourcingQuery,
  type SourcingResult,
} from './types';

const DAILY_LIMIT = 500;
const WINDOW_MS = 24 * 60 * 60 * 1000;
const PROXY_FN = 'ggj_b2b_sourcing_proxy';

interface BucketState {
  timestamps: number[];
}
const rateBuckets = new Map<string, BucketState>();

function acquireToken(orgId: string): boolean {
  const now = Date.now();
  const key = `naukri:${orgId}`;
  const state = rateBuckets.get(key) ?? { timestamps: [] };
  state.timestamps = state.timestamps.filter((t) => now - t < WINDOW_MS);
  if (state.timestamps.length >= DAILY_LIMIT) {
    rateBuckets.set(key, state);
    return false;
  }
  state.timestamps.push(now);
  rateBuckets.set(key, state);
  return true;
}

interface ProxyPeopleResponse {
  people: Array<{
    profileId?: string;
    id?: string;
    externalId?: string;
    name?: string;
    headline?: string;
    designation?: string;
    location?: string;
    profileUrl?: string;
    avatarUrl?: string;
    signals?: Record<string, unknown>;
  }>;
}

export const naukriConnector: SourcingConnector = {
  kind: 'naukri',

  async search(query: SourcingQuery, orgId: string): Promise<SourcingResult[]> {
    if (!orgId) return [];

    const row = await sourceConnectorsService.get(orgId, 'naukri');
    if (!row) throw new ConnectorNotConfiguredError('naukri');
    if (row.status !== 'active') {
      throw new ConnectorNotConfiguredError('naukri', `status=${row.status}`);
    }
    // session_cookie auth is accepted but routes through the proxy which
    // returns empty people + a note (Naukri candidate search requires an RMS
    // API key; a session cookie only supports job search). We let it through
    // so the tool emits ok:true with 0 results rather than an error chip.
    if (row.authKind !== 'api_key' && row.authKind !== 'session_cookie') {
      throw new ConnectorNotConfiguredError(
        'naukri',
        `unsupported auth_kind "${row.authKind}"`,
      );
    }

    if (!acquireToken(orgId)) {
      throw new ConnectorTransportError(
        'naukri',
        `rate limit exceeded (${DAILY_LIMIT}/day) — try again tomorrow`,
      );
    }

    const limit = clampLimit(query.limit);
    const keywords = [
      ...(query.keywords ?? []),
      ...(query.skills ?? []),
    ]
      .map((s) => s.trim())
      .filter(Boolean)
      .join(' ');

    let payload: ProxyPeopleResponse;
    try {
      const { data, error } = await supabase.functions.invoke(PROXY_FN, {
        body: {
          action: 'naukri.search',
          orgId,
          query: {
            keywords,
            location: query.location ?? null,
            experienceYearsMin: query.experienceYearsMin ?? null,
            experienceYearsMax: query.experienceYearsMax ?? null,
            limit,
          },
        },
      });
      if (error) throw error;
      payload = (data ?? { people: [] }) as ProxyPeopleResponse;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'proxy unavailable';
      throw new ConnectorTransportError('naukri', msg);
    }

    void sourceConnectorsService.markUsed(orgId, 'naukri');

    const rows = Array.isArray(payload?.people) ? payload.people : [];
    return rows.slice(0, limit).map((p) => ({
      externalId: String(p.profileId ?? p.externalId ?? p.id ?? ''),
      name: p.name ?? '(unknown)',
      headline: p.headline ?? p.designation,
      locationText: p.location ?? undefined,
      profileUrl: p.profileUrl ?? 'https://www.naukri.com/',
      avatarUrl: p.avatarUrl ?? undefined,
      signals: (p.signals && typeof p.signals === 'object' ? p.signals : {}) as Record<string, unknown>,
      source: 'naukri',
    }));
  },

  async healthcheck(orgId: string): Promise<ConnectorHealth> {
    const row = await sourceConnectorsService.get(orgId, 'naukri');
    if (!row) return { ok: false, reason: 'not configured' };
    if (row.status !== 'active') return { ok: false, reason: row.status };
    return { ok: true };
  },
};
